<template>
  <b-container fluid>
    <b-row align-v="end" class="my-3">
      <b-col cols="12" md="6">
        <label>
          {{ $t('fields.engineering.chromatography.select-standard-or-method') }}
        </label>
        <s-select
          v-model="selectedNorm"
          value-field="id"
          text-field="name"
          :disabled="editMode"
          :options="normsLookup"
          @change="normChanged"
        />
      </b-col>
      <b-col
        class="methods-parameters__select-box"
        cols="12"
        md="6"
      >
        <div class="w-100">
          <label>
            {{ $t('fields.engineering.chromatography.select-method') }}
          </label>
          <s-select
            v-model="selectedMethod"
            value-field="id"
            text-field="name"
            items-child-name="childMethods"
            :disabled="disableMethodDropdown"
            :options="methodsLookup"
            @change="getParameters"
          />
        </div>
        <div
          v-if="userPermission"
          class="methods-parameters__dropdown-menu"
        >
          <dropdown-menu
            v-if="userPermission"
            :methods-lookup="methodsLookup"
            :selected-norm="selectedNorm"
            :selected-method="selectedMethod"
            :asset-general-info="assetGeneralInfo"
            :selected-method-has-hidden-option="selectedMethodHasHiddenOption"
            @handle-visibility="handleVisibility"
            @set-parameters-copy="setParametersCopy"
            @call-edit-parameters="callEditParameters"
            @update-loading="handleLoading"
          />
        </div>
      </b-col>
    </b-row>
    <template v-if="selectedNorm && selectedMethod">
      <component
        :is="viewsByNorm"
        :parameters.sync="parameters"
        :parameters-copy.sync="parametersCopy"
        :methods-lookup="methodsLookup"
        :selected-method-has-hidden-option="selectedMethodHasHiddenOption"
        :selected-method="selectedMethod"
        :selected-norm="selectedNorm"
        :asset-general-info="assetGeneralInfo"
        :edit-mode="editMode"
        :asset-module="assetModule"
        :loading="loading"
        :validation.sync="$v"
        @update-loading="handleLoading"
        @set-parameters-copy="setParametersCopy"
        @call-edit-parameters="callEditParameters"
        @handle-visibility="handleVisibility"
      />
      <b-row align-h="end" class="mt-5 no-internal-print">
        <b-col v-if="editMode" cols="12" md="6" lg="auto" class="px-0 pr-md-1">
          <b-button @click="exitEditMode" block variant="secondary">
            {{ $t('actions.cancel') }}
          </b-button>
        </b-col>
        <b-col v-if="editMode" cols="12" md="6" lg="auto" class="mt-3 mt-md-0 px-0 pl-md-1">
          <b-button
            block
            variant="primary"
            :disabled="disableSave"
            @click="confirmEditParameters"
          >
            {{ $t('actions.save') }}
          </b-button>
        </b-col>
        <b-col v-else-if="userPermission" cols="12" md="6" lg="auto" class="px-0">
          <b-button
            block
            variant="primary"
            @click="enterEditMode"
          >
            {{ $t('actions.edit') }}
          </b-button>
        </b-col>
      </b-row>
    </template>
  </b-container>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import { Rules } from 'sigma-mappings';
import Service from '@/domain/services/installation/engineering/chromatography/parametersOfMethods';
import LoadingMixin from '@/services/loadingMixin';
import MethodsParametersTable from '@/domain/components/installation/engineering/chromatography/methodsParameters/Table';
import MethodsParametersDuvalView from '@/domain/components/installation/engineering/chromatography/methodsParameters/DuvalView';
import DropdownMenu from '@/domain/components/installation/engineering/chromatography/methodsParameters/DropdownMenu';
import fieldsValidations from '@/domain/data/installation/engineering/chromatography/methodsParameters/validations';

const duvalParameters = ['entryCriteria', 'gasesLimit'];

export default {
  name: 'MethodsOfParameters',
  components: { MethodsParametersTable, MethodsParametersDuvalView, DropdownMenu },
  props: {
    assetModule: {
      type: Object,
    },
    assetGeneralInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  validations: fieldsValidations,
  mixins: [LoadingMixin],
  data: vm => ({
    editMode: false,
    normsLookup: [{ id: null, name: vm.$t('general.select') }],
    methodsLookup: [{ id: null, name: vm.$t('general.select') }],
    selectedNorm: null,
    selectedMethod: null,
    parameters: null,
    parametersCopy: null,
  }),
  computed: {
    userPermission() {
      return this.$hasUserPermission('PER013');
    },
    disableMethodDropdown() {
      return !this.selectedNorm || this.editMode;
    },
    viewsByNorm() {
      return this.isDuvalNorm ? MethodsParametersDuvalView : MethodsParametersTable;
    },
    isDuvalNorm() {
      return this.selectedNorm === Rules.DUVAL;
    },
    selectedMethodObject() {
      const currentMethod = this.methodsLookup.find(method => method.id === this.selectedMethod);
      return currentMethod || {};
    },
    selectedMethodHasHiddenOption() {
      return this.selectedMethodObject.hiddenReportsAndAlarms;
    },
    disableSave() {
      const validations = Object.entries(this.$v.parametersCopy);
      const duvalValidations = validations.filter(item => duvalParameters.includes(item[0]));
      const generalValidations = validations.filter(item => !duvalParameters.includes(item[0]));
      const currentValidation = this.isDuvalNorm ? duvalValidations : generalValidations;
      return currentValidation.some(item => item[1].$invalid);
    },
  },
  created() {
    this.setParametersCopy();
    this.getNormsLookup();
  },
  methods: {
    handleLoading(value) {
      if (value) {
        this.startLoading();
        return;
      }
      this.endLoading();
    },
    normChanged(norm) {
      this.parameters = null;
      this.setParametersCopy();
      this.selectedMethod = null;
      if (!norm) {
        return;
      }
      this.getMethodsLookup(norm);
    },
    getNormsLookup() {
      this.startLoading();
      return Service.getNormsLookup()
        .then((response) => {
          this.normsLookup = this.normsLookup.concat(response);
        })
        .finally(() => {
          this.endLoading();
        });
    },
    getMethodsLookup(norm) {
      this.startLoading();
      const params = { norm };
      return Service.getMethodsLookup(this.assetModule.id, params)
        .then((response) => {
          this.methodsLookup = [
            { id: null, name: this.$t('general.select') },
            ...response,
          ];
        })
        .finally(() => {
          this.endLoading();
        });
    },
    getParameters() {
      if (!this.selectedNorm || !this.selectedMethod) {
        this.parameters = null;
        this.setParametersCopy();
        return;
      }
      if (this.isDuvalNorm) {
        this.getDuvalParameters();
        return;
      }
      this.getParametersTable();
    },
    getDuvalParameters() {
      this.startLoading();
      Service.getDuvalParameters(this.assetModule.id, this.selectedMethod).then((response) => {
        this.parameters = response;
        this.setParametersCopy();
      }).finally(() => {
        this.endLoading();
      });
    },
    getParametersTable() {
      this.startLoading();
      Service.getParametersTable(this.assetModule.id, {
        norm: this.selectedNorm,
        method: this.selectedMethod,
      }).then((response) => {
        this.parameters = response;
        this.setParametersCopy();
      }).finally(() => {
        this.endLoading();
      });
    },
    callEditParameters(successAlertCode) {
      this.startLoading();
      Service.editParametersTable(this.assetModule.id, {
        norm: this.selectedNorm,
        method: this.selectedMethod,
        payload: this.parametersCopy,
      }).then(() => {
        this.$pushAlert({ code: successAlertCode });
        this.editMode = false;
        this.getParameters();
      }).finally(() => {
        this.endLoading();
      });
    },
    callEditDuvalParameters() {
      this.startLoading();
      Service.editDuvalParameters(
        this.assetModule.id,
        this.selectedMethod,
        this.parametersCopy,
      ).then(() => {
        this.$pushAlert({ code: 'MS027' });
        this.editMode = false;
        this.getParameters();
      }).finally(() => {
        this.endLoading();
      });
    },
    confirmEditParameters() {
      this.$confirmMessage({
        code: 'MC038',
      }).accepted(() => {
        const call = this.isDuvalNorm ? this.callEditDuvalParameters : this.callEditParameters;
        call('MS021');
      });
    },
    enterEditMode() {
      this.editMode = true;
    },
    exitEditMode() {
      this.$confirmMessage({
        code: 'MC006',
      }).accepted(() => {
        this.editMode = false;
        this.setParametersCopy();
      });
    },
    setParametersCopy() {
      this.parametersCopy = cloneDeep(this.parameters);
    },
    methodHandler(method) {
      return method || this.selectedMethod;
    },
    handleVisibility(hidden, item) {
      if (!this.selectedNorm || !this.selectedMethod) return;
      this.startLoading();
      Service.handleVisibility(this.assetModule.id, {
        hidden,
        norm: this.selectedNorm,
        method: this.methodHandler(item),
      }).then(() => {
        this.$pushAlert({ code: 'MS016' });
        this.getMethodsLookup(this.selectedNorm);
      }).finally(() => {
        this.endLoading();
      });
    },
  },
};
</script>
<style lang="sass" scoped>
@import src/styles/defaults/mobile-first.sass
.methods-parameters
  &__select-box
    display: flex
    align-items: flex-end
    flex-wrap: nowrap
</style>
